function parseNodes(htmlStr) {
  const doc = new DOMParser().parseFromString(htmlStr, "text/html");
  return Array.from(doc.body.childNodes).map((n) => n.cloneNode(true));
}

if (!customElements.get("product-media")) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();
      this.swiperRetryCount = 0;
      this.maxSwiperRetries = 50;
      this.isUpdatingVariant = false;
      this._pendingGalleryUpdate = false;
      this._thumbClickHandler = null;
      this._variantUnsubscribe = null;
      this._variantSubscribeRetryTimer = null;
      this.init();
    }

    init() {
      if (typeof Swiper === "undefined") {
        if (this.swiperRetryCount >= this.maxSwiperRetries) return;
        this.swiperRetryCount++;
        setTimeout(() => this.init(), 100);
        return;
      }

      this.initMainSwiper();
      this.initThumbnailsSwiper();
      this.runWhenReady(() => this.initMediaActionButtons());
      this.removeInvisibleClass();
      this.initActiveVariantMedia();
      this.subscribeToVariantChanges();
    }

    runWhenReady(fn) {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", fn);
      } else {
        fn();
      }
    }

    initMediaActionButtons() {
      const sel = ".product-media-actions-desktop .media-action-button, .product-media-actions-mobile .media-action-button";
      document.querySelectorAll(sel).forEach((btn) => {
        if (btn.dataset.mediaActionBound) return;
        const id = (btn.dataset.sectionId || "").trim();
        if (!id) {
          btn.style.display = "none";
          return;
        }
        const resolveTarget = () =>
          document.getElementById(id) ||
          document.querySelector(`[id^="${CSS.escape(id)}-"]`);
        const target = resolveTarget();
        if (!target) {
          btn.style.display = "none";
          return;
        }
        btn.style.display = "";
        btn.dataset.mediaActionBound = "1";
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          resolveTarget()?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
    }

    initMainSwiper() {
      const mainSwiperEl = this.querySelector(".product-media__images");
      if (!mainSwiperEl) return;

      const prevEl = this.dataset.prevButtonClass || ".swiper-button-prev";
      const nextEl = this.dataset.nextButtonClass || ".swiper-button-next";

      this.mainSwiper = new Swiper(mainSwiperEl, {
        slidesPerView: "auto",
        spaceBetween: 8,
        loop: false,
        centeredSlides: false,
        navigation: { nextEl, prevEl },
        pagination: { el: ".swiper-media-pagination", clickable: true },
        breakpoints: {
          1024: { slidesPerView: 1, spaceBetween: 0, centeredSlides: false },
        },
        on: {
          init: () => this.syncThumbnails(0),
          slideChange: (swiper) => {
            this.syncThumbnails(swiper.activeIndex);
            this.syncVariantFromSlide(swiper.activeIndex);
          },
        },
      });
    }

    initThumbnailsSwiper() {
      const thumbnailsEl = this.querySelector("[data-thumbnails-slider]");
      if (!thumbnailsEl) return;

      if (!this._thumbClickHandler) {
        this._thumbClickHandler = (e) => {
          const thumb = e.target.closest("[data-thumbnails-item]");
          if (!thumb || !this.mainSwiper) return;
          const index = parseInt(thumb.dataset.thumbnailsIndex, 10);
          if (!isNaN(index)) this.mainSwiper.slideTo(index);
        };
        thumbnailsEl.addEventListener("click", this._thumbClickHandler);
      }
    }

    selectVariantById(variantId) {
      if (this.isUpdatingVariant) return;

      const productInfo = this.closest("product-info");
      const variantSelects = productInfo?.querySelector("variant-selects");
      const variantsJson = variantSelects?.querySelector(
        "[data-product-variants]",
      );
      if (!variantsJson) return;

      let variants;
      try {
        variants = JSON.parse(variantsJson.textContent);
      } catch {
        return;
      }

      const targetVariant = variants.find(
        (v) => v.id.toString() === variantId.toString(),
      );
      if (!targetVariant) return;

      this.isUpdatingVariant = true;

      targetVariant.options.forEach((optionValue, index) => {
        const optionPosition = index + 1;
        for (const radio of variantSelects.querySelectorAll(
          `input[type="radio"][name*="-${optionPosition}"]`,
        )) {
          if (radio.value === optionValue && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
            break;
          }
        }

        const selectEl = variantSelects.querySelector(
          `select[name*="options"]`,
        );
        if (selectEl) {
          for (const option of selectEl.querySelectorAll("option")) {
            if (option.value === optionValue && !option.selected) {
              option.selected = true;
              selectEl.dispatchEvent(new Event("change", { bubbles: true }));
              break;
            }
          }
        }
      });

      setTimeout(() => {
        this.isUpdatingVariant = false;
      }, 100);
    }

    syncThumbnails(activeIndex) {
      this.querySelectorAll("[data-thumbnails-item]").forEach((thumb, i) => {
        thumb.classList.toggle("active_picture", i === activeIndex);
      });
    }

    syncVariantFromSlide(activeIndex) {
      if (this.isUpdatingVariant) return;
      const slides = this.querySelectorAll("[data-media-item-index]");
      const variantId = slides[activeIndex]?.dataset.variantId;
      if (variantId) this.selectVariantById(variantId);
    }

    removeInvisibleClass() {
      this.querySelector("#main-container")?.classList.remove("invisible");
      this.querySelector("#thumbnails-container")?.classList.remove(
        "invisible",
      );
    }

    initActiveVariantMedia() {
      const variantIdFromUrl = new URLSearchParams(window.location.search).get(
        "variant",
      );
      if (!variantIdFromUrl || !this.mainSwiper) return;

      const galleries = this.getVariantGalleries();
      if (galleries?.[variantIdFromUrl]?.length > 0) return;

      const slides = Array.from(
        this.querySelectorAll("[data-media-item-index]"),
      );
      const targetIndex = slides.findIndex(
        (s) => s.dataset.variantId === variantIdFromUrl,
      );
      if (targetIndex < 0) return;

      this.isUpdatingVariant = true;
      this.mainSwiper.slideTo(targetIndex, 0);
      this.syncThumbnails(targetIndex);
      setTimeout(() => {
        this.isUpdatingVariant = false;
      }, 200);
    }

    subscribeToVariantChanges() {
      if (
        typeof subscribe !== "undefined" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        if (this._variantSubscribeRetryTimer != null) {
          clearTimeout(this._variantSubscribeRetryTimer);
          this._variantSubscribeRetryTimer = null;
        }
        if (this._variantUnsubscribe) {
          this._variantUnsubscribe();
          this._variantUnsubscribe = null;
        }
        this._variantUnsubscribe = subscribe(
          PUB_SUB_EVENTS.variantChange,
          ({ data }) => {
            if (data?.variant) this.updateGalleryForVariant(data.variant.id);
          },
        );
      } else {
        if (this._variantSubscribeRetryTimer != null) {
          clearTimeout(this._variantSubscribeRetryTimer);
        }
        this._variantSubscribeRetryTimer = setTimeout(() => {
          this._variantSubscribeRetryTimer = null;
          this.subscribeToVariantChanges();
        }, 100);
      }
    }

    getVariantGalleries() {
      const script = document.querySelector("[data-variant-galleries]");
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch {
        return null;
      }
    }

    getProductMediaFallback() {
      const script = document.querySelector("[data-product-media-fallback]");
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch {
        return null;
      }
    }

    buildImageSlide(index, url, alt, mediaId, variantId) {
      const variantAttr = variantId ? ` data-variant-id="${variantId}"` : "";
      return `
        <div
          class="swiper-slide product-media__item flex items-center !w-full overflow-hidden"
          data-media-item="${alt}"
          data-media-item-index="${index}"
          data-media-id="${mediaId}"${variantAttr}
          tabindex="0"
        >
          <picture class="aspect-square bg-white">
            <img
              src="${url}"
              alt="${alt}"
              class="w-full h-full aspect-square object-contain"
              loading="lazy"
              width="2000"
              height="2000"
            >
          </picture>
        </div>`;
    }

    buildThumbSlide(index, thumbUrl, alt, isFirst) {
      const activeClass = isFirst ? " active_picture" : "";
      return `
        <div
          data-thumbnails-item
          data-thumbnails-index="${index}"
          class="swiper-slide flex hover:cursor-pointer !h-auto aspect-square relative${activeClass}"
          data-thumbnails-alt="${alt}"
        >
          <picture class="w-full h-full aspect-square bg-white outline-1 outline-black outline-offset-[-2px]">
            <img
              src="${thumbUrl}"
              alt="${alt}"
              class="aspect-square object-contain w-full h-full"
              loading="lazy"
              width="100"
              height="100"
            >
          </picture>
        </div>`;
    }

    updateGalleryForVariant(variantId) {
      const variantIdStr = variantId.toString();
      const galleries = this.getVariantGalleries();
      const fallback = this.getProductMediaFallback();

      let images = [];
      if (galleries?.[variantIdStr]?.length > 0) {
        images = galleries[variantIdStr].map((img, i) => ({
          url: img.url,
          thumb: img.thumb,
          alt: img.alt,
          mediaId: `vg-${i + 1}`,
          variantId: null,
        }));
      } else if (fallback?.length > 0) {
        const variantImages = fallback.filter(
          (img) => !img.variantId || img.variantId.toString() === variantIdStr,
        );
        const pool = variantImages.length > 0 ? variantImages : fallback;
        images = pool.map((img) => ({
          url: img.url,
          thumb: img.thumb,
          alt: img.alt,
          mediaId: img.id,
          variantId: img.variantId,
        }));
      }

      if (!images.length) return;

      this.isUpdatingVariant = true;
      this._pendingGalleryUpdate = true;

      const mainWrapper = this.querySelector("[data-media-wrapper]");
      if (mainWrapper) {
        mainWrapper.replaceChildren(
          ...parseNodes(
            images
              .map((img, i) =>
                this.buildImageSlide(
                  i,
                  img.url,
                  img.alt,
                  img.mediaId,
                  img.variantId,
                ),
              )
              .join(""),
          ),
        );
      }

      const thumbsWrapper = this.querySelector(
        "[data-thumbnails-slider] .swiper-wrapper",
      );
      if (thumbsWrapper) {
        thumbsWrapper.replaceChildren(
          ...parseNodes(
            images
              .map((img, i) =>
                this.buildThumbSlide(i, img.thumb, img.alt, i === 0),
              )
              .join(""),
          ),
        );
      }

      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }

      requestAnimationFrame(() => {
        this.initMainSwiper();
        this.syncThumbnails(0);

        const swiperSlider = this.querySelector("[data-thumbnails-slider]");
        if (swiperSlider?.swiper) {
          swiperSlider.swiper.update();
          swiperSlider.swiper.slideTo(0, 0);
          swiperSlider.updateThumbnailNavigation?.();
        }

        this._pendingGalleryUpdate = false;
        setTimeout(() => {
          this.isUpdatingVariant = false;
        }, 100);
      });
    }

    setActiveMedia(mediaId) {
      if (this._pendingGalleryUpdate || !this.mainSwiper) return;
      const slides = Array.from(
        this.querySelectorAll("[data-media-item-index]"),
      );
      const targetIndex = Math.max(
        0,
        slides.findIndex((s) => s.dataset.mediaId === mediaId.toString()),
      );
      this.isUpdatingVariant = true;
      this.mainSwiper.slideTo(targetIndex);
      setTimeout(() => {
        this.isUpdatingVariant = false;
      }, 100);
    }

    disconnectedCallback() {
      if (this._variantSubscribeRetryTimer != null) {
        clearTimeout(this._variantSubscribeRetryTimer);
        this._variantSubscribeRetryTimer = null;
      }
      if (this._variantUnsubscribe) {
        this._variantUnsubscribe();
        this._variantUnsubscribe = null;
      }
    }
  }

  customElements.define("product-media", ProductMedia);
}

if (!customElements.get("video-slider")) {
  class VideoSlider extends HTMLElement {
    constructor() {
      super();
      this.setupSwipeAreas();
    }

    setupSwipeAreas() {
      this.querySelectorAll(
        ".swipe-left, .swipe-right, .swipe-top, .swipe-bottom",
      ).forEach((area) => {
        area.addEventListener("click", (e) => {
          e.stopPropagation();
          const productMedia = this.closest("product-media");
          if (!productMedia?.mainSwiper) return;
          if (area.classList.contains("swipe-left"))
            productMedia.mainSwiper.slidePrev();
          else if (area.classList.contains("swipe-right"))
            productMedia.mainSwiper.slideNext();
        });
      });
    }
  }

  customElements.define("video-slider", VideoSlider);
}

if (!customElements.get("swiper-slider")) {
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.initSwiper();
    }

    initSwiper() {
      if (typeof Swiper === "undefined") {
        setTimeout(() => this.initSwiper(), 100);
        return;
      }

      const swiperEl = this.classList.contains("swiper")
        ? this
        : this.querySelector(".swiper");
      if (!swiperEl) return;

      let options = {
        spaceBetween: 12,
        direction: "horizontal",
      };

      if (this.dataset.options) {
        try {
          const dataOptions = JSON.parse(this.dataset.options);
          options = { ...options, ...dataOptions };
        } catch {}
      }

      if (!options.slidesPerView && !options.breakpoints) {
        options.slidesPerView = "auto";
      }

      const { nextButtonClass, prevButtonClass } = this.dataset;
      if (nextButtonClass && prevButtonClass) {
        options.navigation = {
          nextEl: nextButtonClass,
          prevEl: prevButtonClass,
        };
      }

      this.swiper = new Swiper(swiperEl, options);

      if (this.hasAttribute("data-thumbnails-slider")) {
        const applyThumbSlides = () => {
          const n = window.innerWidth >= 1280 ? 6 : 5;
          if (this.swiper && this.swiper.params.slidesPerView !== n) {
            this.swiper.params.slidesPerView = n;
            this.swiper.update();
          }
          this.updateThumbnailNavigation();
        };
        applyThumbSlides();
        this.swiper.on("resize", () => this.updateThumbnailNavigation());
        this.swiper.on("update", () => this.updateThumbnailNavigation());
        const onResize = () => {
          clearTimeout(this._thumbResizeTid);
          this._thumbResizeTid = setTimeout(applyThumbSlides, 80);
        };
        window.addEventListener("resize", onResize);
        this._thumbResizeCleanup = () =>
          window.removeEventListener("resize", onResize);
      }
    }

    getThumbnailNavButtons() {
      const { nextButtonClass, prevButtonClass } = this.dataset;
      return [
        nextButtonClass ? this.querySelector(nextButtonClass) : null,
        prevButtonClass ? this.querySelector(prevButtonClass) : null,
      ].filter(Boolean);
    }

    getThumbnailVisibleLimit() {
      if (!this.swiper) return 5;
      const spv = this.swiper.params.slidesPerView;
      return typeof spv === "number" ? spv : 5;
    }

    updateThumbnailNavigation() {
      if (!this.hasAttribute("data-thumbnails-slider") || !this.swiper) return;

      const slideCount = this.swiper.slides?.length ?? 0;
      const needsScroll = slideCount > this.getThumbnailVisibleLimit();
      const navButtons = this.getThumbnailNavButtons();

      navButtons.forEach((btn) => {
        btn.classList.toggle("thumb-nav--hidden", !needsScroll);
      });
      this.classList.add("thumb-nav--ready");
    }

    disconnectedCallback() {
      if (this._thumbResizeCleanup) {
        this._thumbResizeCleanup();
        this._thumbResizeCleanup = null;
      }
    }
  }

  customElements.define("swiper-slider", SwiperSlider);
}
